import type { WavesChart } from '@shared/wavesChart';
import type { WavesLayout } from '../wavesPlayfieldGeometry';
import { playerCanvasX, playfieldYToCanvas } from '../wavesPlayfieldGeometry';
import {
  BEAT_PULSE_STRENGTH,
  PLAYER_HOLD_GLOW_RADIUS_MUL,
} from './wavesRenderConstants';
import type { WavesTrailSample } from './wavesRenderTypes';
import { hexToRgba } from './wavesRenderColour';

export interface WavesTrailView {
  samples: readonly WavesTrailSample[];
  /** Ring-buffer write head when `capacity < samples.length`. */
  startIdx: number;
  count: number;
  capacity: number;
}

/**
 * Paint the player + recent trajectory trail. Pressing → upward
 * triangle with a brighter glow halo; released → downward triangle.
 */
export function drawWavesPlayer(
  ctx: CanvasRenderingContext2D,
  layout: WavesLayout,
  chart: WavesChart,
  state: { playerY: number },
  pressing: boolean,
  trail: WavesTrailView,
  beatPulse: number,
  edgeAnim: number,
  chromeShadows: boolean,
): void {
  const playerX = playerCanvasX(layout);
  const playerY = playfieldYToCanvas(layout, state.playerY);
  const playerRadius = chart.playerRadius * layout.scaleY;
  const palette = chart.palette;
  const pulse = beatPulse * BEAT_PULSE_STRENGTH;

  drawTrail(ctx, layout, trail, playerX, palette.player, pressing, chromeShadows);

  const glowRadius = playerRadius * PLAYER_HOLD_GLOW_RADIUS_MUL * (1 + pulse * 0.15);
  const glowAlpha = pressing ? 0.55 + pulse * 0.2 : 0.32 + pulse * 0.12;
  if (chromeShadows) {
    ctx.save();
    const glowGrad = ctx.createRadialGradient(
      playerX,
      playerY,
      playerRadius * 0.5,
      playerX,
      playerY,
      glowRadius,
    );
    glowGrad.addColorStop(0, hexToRgba(palette.player, glowAlpha));
    glowGrad.addColorStop(1, hexToRgba(palette.player, 0));
    ctx.fillStyle = glowGrad;
    ctx.beginPath();
    ctx.arc(playerX, playerY, glowRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  } else {
    ctx.save();
    ctx.fillStyle = hexToRgba(palette.player, glowAlpha * 0.55);
    ctx.beginPath();
    ctx.arc(playerX, playerY, glowRadius * 0.75, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  const squashY = 1 + edgeAnim * 0.18;
  const stretchX = 1 - edgeAnim * 0.1;

  ctx.save();
  ctx.translate(playerX, playerY);
  ctx.scale(stretchX, squashY);
  ctx.translate(-playerX, -playerY);
  ctx.fillStyle = palette.player;
  if (chromeShadows) {
    ctx.shadowColor = palette.player;
    ctx.shadowBlur = 10 + pulse * 12;
  }
  ctx.beginPath();
  if (pressing) {
    ctx.moveTo(playerX, playerY - playerRadius);
    ctx.lineTo(playerX - playerRadius * 0.85, playerY + playerRadius * 0.6);
    ctx.lineTo(playerX + playerRadius * 0.85, playerY + playerRadius * 0.6);
  } else {
    ctx.moveTo(playerX, playerY + playerRadius);
    ctx.lineTo(playerX - playerRadius * 0.85, playerY - playerRadius * 0.6);
    ctx.lineTo(playerX + playerRadius * 0.85, playerY - playerRadius * 0.6);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function trailSampleAt(view: WavesTrailView, index: number): WavesTrailSample {
  if (view.count < view.capacity) {
    return view.samples[index];
  }
  return view.samples[(view.startIdx + index) % view.capacity];
}

function drawTrail(
  ctx: CanvasRenderingContext2D,
  layout: WavesLayout,
  view: WavesTrailView,
  headX: number,
  trailColour: string,
  pressing: boolean,
  chromeShadows: boolean,
): void {
  if (view.count < 2) {
    return;
  }
  ctx.save();
  ctx.lineWidth = pressing ? 5 : 4;
  ctx.lineCap = 'round';
  ctx.strokeStyle = trailColour;
  if (chromeShadows) {
    ctx.shadowColor = trailColour;
    ctx.shadowBlur = pressing ? 14 : 8;
  }
  for (let i = 0; i < view.count - 1; i += 1) {
    const ageA = (view.count - 1 - i) / view.count;
    const ageB = (view.count - 2 - i) / view.count;
    const a = trailSampleAt(view, i);
    const b = trailSampleAt(view, i + 1);
    const xa = headX - ageA * 60;
    const xb = headX - ageB * 60;
    const ya = playfieldYToCanvas(layout, a.playerY);
    const yb = playfieldYToCanvas(layout, b.playerY);
    ctx.globalAlpha = Math.max(0, 1 - ageA);
    ctx.beginPath();
    ctx.moveTo(xa, ya);
    ctx.lineTo(xb, yb);
    ctx.stroke();
  }
  ctx.restore();
}
